import React from 'react';
import { Modal, Tabs, Button, Row } from 'antd';
import GeneralTab, { GeneralTabRef } from '../../MapSettings/GeneralTab';
import PermissionsTab from '../../MapSettings/PermissionsTab';
import ToolBoxTab from '../../MapSettings/ToolBoxTab';
import SettingsPage from '../../MapSettings/ToolBoxTab/QuerySettingPage';
import { ModalVisibility } from '../index';

const { TabPane } = Tabs;

interface MapSettingsModalProps {
  visible: boolean;
  onCancel: () => void;
  onSave: () => void;
  mapSettings: any;
  updateSettings: (section: string, key: string, value: any) => void;
  generalTabRef: React.RefObject<GeneralTabRef>;
  modalVisibility: ModalVisibility;
  showSubModal: (type: keyof ModalVisibility) => void;
  hideSubModal: (type: keyof ModalVisibility) => void;
  mapScreenshot: string;
  onCapture: () => void;
  onMapPositionClick: (position: string) => void;
  mapId: string;
  drawerItems: any[];
  selectedColumns: any;
  setSelectedColumns: (cols: any) => void;
  isNewMap?: boolean; // New prop to distinguish mode
}

const MapSettingsModal: React.FC<MapSettingsModalProps> = ({
  visible,
  onCancel,
  onSave,
  mapSettings,
  updateSettings,
  generalTabRef,
  modalVisibility,
  showSubModal,
  hideSubModal,
  mapScreenshot,
  onCapture,
  onMapPositionClick,
  mapId,
  drawerItems,
  selectedColumns,
  setSelectedColumns,
  isNewMap = false,
}) => {
  const [currentPage, setCurrentPage] = React.useState<'toolbox' | 'settings'>('toolbox');
  const [toolType, setToolType] = React.useState<string>('');

  // Only allow opening detailed settings if layers exist (usually not for new maps)
  const openSettingsPage = (type: string) => {
    if (drawerItems.length > 0) {
      setToolType(type);
      setCurrentPage('settings');
    } else {
      console.warn('Cannot configure tool settings: No layers available.');
    }
  };

  const goBackToToolBox = () => setCurrentPage('toolbox');

  // Reset to main view when modal closes/opens
  React.useEffect(() => {
    if (visible) setCurrentPage('toolbox');
  }, [visible]);

  return (
    <Modal
      title={isNewMap ? 'CREATE NEW MAP' : 'MAP SETTINGS'}
      open={visible}
      onCancel={onCancel}
      footer={null}
      centered
      destroyOnClose
      className="custom-settings-modal"
      width={1120}
    >
      {currentPage === 'toolbox' ? (
        <Tabs defaultActiveKey="1" className="custom-tabs">
          <TabPane tab="GENERAL" key="1" className="custom-tab-pane">
            <GeneralTab
              ref={generalTabRef}
              settings={mapSettings.general}
              updateSettings={(key: any, value: any) => updateSettings('general', key, value)}
              isThumbnailModalVisible={modalVisibility.thumbnail}
              showModal={showSubModal}
              handleCancel={hideSubModal}
              mapScreenshot={mapScreenshot}
              onCapture={onCapture}
              onMapPositionClick={onMapPositionClick}
            />
          </TabPane>
          <TabPane tab="PERMISSIONS" key="2" className="custom-tab-pane">
            <PermissionsTab
              settings={mapSettings.permissions}
              updateSettings={(key: any, value: any) => updateSettings('permissions', key, value)}
              isDeleteModalVisible={modalVisibility.delete}
              isPasswordModalVisible={modalVisibility.password}
              isGroupModalVisible={modalVisibility.group}
              showModal={showSubModal}
              handleCancel={hideSubModal}
              mapId={mapId}
            />
          </TabPane>
          <TabPane tab="TOOLBOX" key="3" className="custom-tab-pane">
            <ToolBoxTab
              openSettings={openSettingsPage}
              settings={mapSettings.toolbox}
              updateSettings={(key: any, value: any) => updateSettings('toolbox', key, value)}
              columns={selectedColumns}
              setColumns={setSelectedColumns}
            />
          </TabPane>
        </Tabs>
      ) : (
        /* Only render settings page if not a new map (or if layers exist) */
        !isNewMap &&
        drawerItems.length > 0 && <SettingsPage goBack={goBackToToolBox} layers={drawerItems} toolType={toolType} setColumns={setSelectedColumns} />
      )}

      <Row justify="center" className="map-settings-modal__footer-row">
        <Button onClick={onSave} className="custom-button save-button">
          {isNewMap ? 'Create Map' : 'Save and Close'}
        </Button>
      </Row>
    </Modal>
  );
};

export default MapSettingsModal;
